/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { Component, OnInit } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LicenceApiService } from '../../../shared/http';
import { LicenceModel } from '../../../shared/models';
import { LicenceService } from '../../../shared/services/utilities';
import { LoadingService, LogService, NotifyService, RbacService } from '../../../../../core/services';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

declare let Intercom: any;

@Component({
    selector: 'app-licence',
    templateUrl: './licence.component.html',
    styleUrls: ['./licence.component.scss']
})

export class LicenceComponent extends BaseComponent implements OnInit {
    /**
     * To store the licence form.
     */
    public licenceForm: FormGroup;

    /**
     * To store the licence data.
     */
    public licenceData: LicenceModel;

    /**
     * To store the licence model name.
     */
    public licenceModelName: string;

    /**
     * A string to store a yes or no value if GSLB is enabled or not.
     */
    public gslbEnabled: string = '';

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        private formBuilder: FormBuilder,
        private licenceApiService: LicenceApiService,
        private licenceService: LicenceService,
        private loadingService: LoadingService,
        private logService: LogService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
    ) {
        super();
        this.licenceData = this.route.snapshot.data['licenceInfo'];
        this.setGSLBEnabledValue();
        this.setUpForm();
        this.registerButtons();

        this.licenceModelName = this.licenceService.getApplianceModelName();
    }

    public ngOnInit(): void {
        this.licenceService.licenceObj = this.licenceData;
    }

    /**
     * Update the licence key.
     */
    public updateLicence(): Observable<any> {
        this.handleNotifications();
        this.notifyService.muteLocalNotifications();

        return new Observable(() => {
            const licenceKey = this.licenceForm.value.licenceKey;
            this.licenceApiService.updateLicenceDetails(licenceKey)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.licenceApiService.getLicenceDetails()
                            .pipe(takeUntil(this.destroyed$))
                            .subscribe({
                                next: (response: LicenceModel): void => {
                                    this.licenceData = response;
                                    this.licenceService.licenceObj = this.licenceData;
                                },
                                error: (error: ErrorResponseModel): void => {
                                    this.notifyService.unmuteLocalNotifications();
                                    this.logService.error('LicenceComponent: getLicenceDetails',
                                        'Unable to get licence details', error);
                                }
                            });
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.notifyService.unmuteLocalNotifications();
                        this.logService.error('LicenceComponent: updateLicence', 'Unable to update licence', error);
                        // Replace API notifications with custom message.
                        this.notifyService.error('You\'ve entered an invalid license key. <br>' +
                            error.message, { id: 'invalidLicenceKey' });
                    },
                })
                .add((): void => {
                    this.loadingService.loadingButtonEmitter.next(false);
                    this.notifyService.removeNotificationById('updateLicenceKey');
                });
        });
    }

    /**
     * Handle the notifications.
     */
    public handleNotifications(): void {
        this.notifyService.info('Updating licence key...', { id: 'updateLicenceKey' });
        const notifications = this.notifyService.getCopyOfNotifications();
        const invalidLicenceNotification = notifications.find(notification => notification.id === 'invalidLicenceKey');
        if (!invalidLicenceNotification) {
            return;
        } else {
            this.notifyService.removeNotificationById(invalidLicenceNotification.id);
        }
    }

    /**
     * Open intercom and pre-populate with sales question.
     */
    public contactSales(): void {
        Intercom('showNewMessage', 'Hello! Can I get some help regarding my appliance license, please?');
    }

    /**
     * Set up the licence form.
     * @private
     */
    private setUpForm(): void {
        this.licenceForm = this.formBuilder.group({
            licenceKey: [null, Validators.required]
        });
    }

    /**
     * Set the GSLB enabled text value.
     * @private
     */
    private setGSLBEnabledValue(): void {
        const lbServices = JSON.parse(localStorage.getItem('lbServices'));

        if (!lbServices?.gslb || lbServices.gslb === 'disabled') {
            this.gslbEnabled = 'No';
        } else {
            this.gslbEnabled = 'Yes';
        }
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'license-submit': [{ path: '/licence', methods: ['PUT'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup['license-submit']) {
            this.licenceForm.disable();
        }
    }
}
